import { Spin, Typography, theme } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    fitScale,
    PREVIEW_FRAME_HEIGHT,
    PREVIEW_FRAME_NAME,
    PREVIEW_FRAME_WIDTH,
} from '../../domain/categoryPermissions';

interface PagePreviewProps {
    /** The route to show, e.g. `/pred_casa`. */
    path: string | null;
    /** The page's name, for the frame's accessible title. */
    title: string;
}

/**
 * A page shown small, as it appears in the application.
 *
 * The page is the real one, loaded in an iframe laid out at desktop size and scaled down to the
 * width available. It is for looking only: the frame takes no pointer input, is sandboxed so it
 * cannot open dialogs or navigate the window around it, and runs without the application's chrome
 * (`App` recognises the frame by its name).
 */
const PagePreview = ({ path, title }: PagePreviewProps) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [loadedPath, setLoadedPath] = useState<string | null>(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return undefined;
        }

        setContainerWidth(container.clientWidth);
        const observer = new ResizeObserver(([entry]) =>
            setContainerWidth(entry.contentRect.width),
        );
        observer.observe(container);

        return () => observer.disconnect();
    }, []);

    const scale = fitScale(containerWidth);

    return (
        <div ref={containerRef} style={{ width: '100%' }} data-testid="page-preview">
            {path === null ? (
                <Typography.Text type="secondary" data-testid="page-preview-empty">
                    {t('configuration.permissions.previewEmpty')}
                </Typography.Text>
            ) : (
                <div
                    style={{
                        position: 'relative',
                        width: PREVIEW_FRAME_WIDTH * scale,
                        height: PREVIEW_FRAME_HEIGHT * scale,
                        overflow: 'hidden',
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                        background: token.colorBgContainer,
                    }}
                >
                    {loadedPath !== path && (
                        <Spin
                            style={{ position: 'absolute', inset: 0, margin: 'auto', zIndex: 1 }}
                        />
                    )}
                    <iframe
                        // Remounted per page, so the previous page never lingers while the next loads.
                        key={path}
                        name={PREVIEW_FRAME_NAME}
                        title={t('configuration.permissions.previewFrameTitle', { page: title })}
                        src={path}
                        sandbox="allow-same-origin allow-scripts"
                        tabIndex={-1}
                        onLoad={() => setLoadedPath(path)}
                        data-testid="page-preview-frame"
                        style={{
                            width: PREVIEW_FRAME_WIDTH,
                            height: PREVIEW_FRAME_HEIGHT,
                            border: 0,
                            transform: `scale(${scale})`,
                            transformOrigin: '0 0',
                            pointerEvents: 'none',
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default PagePreview;
